import { z } from 'zod'
import { communityObject } from '~/packages/geo/community/communityObject'
import { dateOrIsoString } from '~/prix/types/date'
import { enumerated } from '~/prix/types/enumerated'
import { latitude, longitude } from '~/prix/types/presets/geo'
import { clientObject } from '../client/clientObject'

// Todos os tipos de POI disponíveis, do OSM
export const pointOfInterestOSMTypeMap: Record<string, string> = {
  // Sustentação (Sustenance)
  bar: 'Bar',
  biergarten: 'Biergarten (Jardim de Cerveja)',
  cafe: 'Café',
  fast_food: 'Fast Food',
  food_court: 'Praça de Alimentação',
  ice_cream: 'Sorveteria',
  pub: 'Pub',
  restaurant: 'Restaurante',

  // Educação (Education)
  college: 'Faculdade',
  dancing_school: 'Escola de Dança',
  driving_school: 'Autoescola',
  first_aid_school: 'Escola de Primeiros Socorros',
  kindergarten: 'Jardim de Infância',
  language_school: 'Escola de Idiomas',
  library: 'Biblioteca',
  surf_school: 'Escola de Surfe',
  toy_library: 'Brinquedoteca',
  research_institute: 'Instituto de Pesquisa',
  training: 'Centro de Treinamento',
  music_school: 'Escola de Música',
  school: 'Escola',
  traffic_park: 'Parque de Trânsito',
  university: 'Universidade',

  // Transporte (Transportation)
  bicycle_parking: 'Estacionamento de Bicicletas',
  bicycle_repair_station: 'Estação de Reparo de Bicicletas',
  bicycle_rental: 'Aluguel de Bicicletas',
  bicycle_wash: 'Lavagem de Bicicletas',
  boat_rental: 'Aluguel de Barcos',
  boat_storage: 'Armazenamento de Barcos',
  boat_sharing: 'Compartilhamento de Barcos',
  bus_station: 'Terminal Rodoviário',
  car_rental: 'Aluguel de Carros',
  car_sharing: 'Compartilhamento de Carros',
  car_wash: 'Lava-jato',
  compressed_air: 'Ar Comprimido',
  vehicle_inspection: 'Inspeção Veicular',
  charging_station: 'Estação de Recarga Elétrica',
  driver_training: 'Treinamento de Motoristas',
  ferry_terminal: 'Terminal de Balsas',
  fuel: 'Posto de Combustível',
  grit_bin: 'Caixa de Sal/Areia',
  motorcycle_parking: 'Estacionamento de Motos',
  parking: 'Estacionamento',
  parking_entrance: 'Entrada de Estacionamento',
  parking_space: 'Vaga de Estacionamento',
  taxi: 'Ponto de Táxi',
  weighbridge: 'Balança Rodoviária',

  // Financeiro (Financial)
  atm: 'Caixa Eletrônico (ATM)',
  payment_terminal: 'Terminal de Pagamento',
  bank: 'Banco',
  bureau_de_change: 'Casa de Câmbio',
  money_transfer: 'Transferência de Dinheiro',
  payment_centre: 'Centro de Pagamento',

  // Saúde (Healthcare)
  baby_hatch: 'Berço de Segurança',
  clinic: 'Clínica',
  dentist: 'Dentista',
  doctors: 'Unidade de Saúde', // "Médicos" é o nome da tag do OSM, mas o nome mais amigável para exibir é "Unidade de Saúde"
  hospital: 'Hospital',
  nursing_home: 'Casa de Repouso',
  pharmacy: 'Farmácia',
  social_facility: 'Equipamento Social',
  veterinary: 'Veterinário',

  // Entretenimento e Cultura (Entertainment, Arts & Culture)
  arts_centre: 'Centro de Artes',
  brothel: 'Bordel',
  casino: 'Cassino',
  cinema: 'Cinema',
  community_centre: 'Centro Comunitário',
  conference_centre: 'Centro de Convenções',
  events_venue: 'Local de Eventos',
  exhibition_centre: 'Centro de Exposições',
  fountain: 'Chafariz / Fonte',
  gambling: 'Casa de Jogos',
  love_hotel: 'Motel',
  music_venue: 'Casa de Shows',
  night_club: 'Boate',
  planetarium: 'Planetário',
  social_centre: 'Centro Social',
  studio: 'Estúdio',
  theater: 'Teatro',

  // Serviços Públicos (Public Service)
  courthouse: 'Fórum / Tribunal',
  fire_station: 'Corpo de Bombeiros',
  police: 'Polícia',
  post_box: 'Caixa de Correio',
  post_depot: 'Centro de Distribuição Postal',
  post_office: 'Agência de Correios',
  prison: 'Prisão',
  ranger_station: 'Posto de Guardas-Florestais',
  townhall: 'Prefeitura',

  // Instalações (Facilities)
  bbq: 'Churrasqueira',
  bench: 'Banco (Assento)',
  check_in: 'Check-in',
  dog_toilet: 'Sanitário para Cães',
  dressing_room: 'Vestiário / Provador',
  drinking_water: 'Água Potável / Bebedouro',
  give_box: 'Caixa de Doação',
  lounge: 'Sala de Estar / Lounge',
  mailroom: 'Sala de Correspondência',
  parcel_locker: 'Armário de Encomendas',
  shelter: 'Abrigo',
  shower: 'Chuveiro',
  telephone: 'Telefone Público',
  toilets: 'Banheiros',
  water_point: 'Ponto de Água',
  watering_place: 'Bebedouro para Animais',

  // Gestão de Resíduos (Waste Management)
  sanitary_dump_station: 'Estação de Despejo Sanitário',
  recycling: 'Reciclagem',
  waste_basket: 'Lixeira',
  waste_disposal: 'Descarte de Resíduos',
  waste_transfer_station: 'Estação de Transbordo de Lixo',

  // Outros (Others)
  animal_boarding: 'Hospedagem de Animais',
  animal_breeding: 'Criação de Animais',
  animal_shelter: 'Abrigo de Animais',
  animal_training: 'Treinamento de Animais',
  baking_oven: 'Forno de Panificação',
  clock: 'Relógio Público',
  crematorium: 'Crematório',
  dive_centre: 'Centro de Mergulho',
  funeral_hall: 'Sala Funerária / Velório',
  grave_yard: 'Cemitério',
  hunting_stand: 'Posto de Caça',
  internet_cafe: 'Lan House',
  kitchen: 'Cozinha',
  kneipp_water_cure: 'Instalação Hidroterápica',
  lounger: 'Espreguiçadeira',
  marketplace: 'Mercado / Feira Livre',
  monastery: 'Mosteiro',
  mortuary: 'Necrotério',
  photo_booth: 'Cabine de Fotos',
  place_of_mourning: 'Local de Luto',
  place_of_worship: 'Local de Culto',
  public_bath: 'Banho Público',
  public_bookcase: 'Biblioteca Livre',
  stage: 'Palco',
  vending_machine: 'Máquina de Venda Automática',
  hydrant: 'Hidrante',
}

const availableTypes = [
  'school',
  'hospital',
  'clinic',
  'doctors',
  'pharmacy',
  'bank',
  'police',
  'fire_station',
  'townhall',
  'community_centre',
  'place_of_worship',
  'fuel',
  'restaurant',
  'marketplace',
  'post_office',
  'library',
  'kindergarten',
  'college',
  'university',
  'parking',
  'social_centre',
  'social_facility',
  'sports_centre',
  'gym',
  'grave_yard',
  'veterinary',
  'car_wash',
  'driving_school',
  'courthouse',
] as const

export const pointOfInterestAvailableTypeMap = Object.fromEntries(
  availableTypes.map(key => [key, pointOfInterestOSMTypeMap[key]]),
) as Record<(typeof availableTypes)[number], string>

export const pointOfInterestObject = z.object({
  id: z.string().uuid(),
  code: z.number().nullish(),
  plusCode: z.string(),
  title: z.string().nullish(),
  observation: z.string().nullish(),
  whatsapp: z.string().nullish(),
  alternatePhone: z.string().nullish(),
  wifiSsid: z.string().nullish(),
  wifiPassword: z.string().nullish(),
  latitude: latitude(),
  longitude: longitude(),
  accuracy: z.number().nullish(),
  type: enumerated(pointOfInterestAvailableTypeMap),
  communityId: z.string().uuid().nullish(),
  community: communityObject.nullish(),
  buildingId: z.number().nullish(),
  fullAddress: z.string().nullish(),
  insertUserId: z.string().nullish(),
  editUserId: z.string().nullish(),
  sourceFilter: z.string().nullish(),
  clientId: clientObject.shape.id,
  createdAt: dateOrIsoString().nullish(),
  updatedAt: dateOrIsoString().nullish(),
  synchronizedAt: dateOrIsoString().nullish(),
  deletedAt: dateOrIsoString().nullish(),
})

export type PointOfInterestType = z.infer<typeof pointOfInterestObject>
